/**
 * Souls page composable - matches original script.js exactly
 * Keeps all data and methods in one place to avoid mismatches
 */
import { ref, reactive, onMounted, onBeforeUnmount } from '@vue/composition-api';
import axios from 'axios';
import Vue from 'vue';
import { getters, mutations } from "@/components/game/store";
import { bannerProps } from "@/components/game/shared/baseBanner";
import eventBus from "@/components/game/store/eventBus";
import { HOST_URL, API_V2 } from '@/config/api';
import { useTutorial } from '../../composables/tutorial';

export function useSoulsPage(props) {
  // Tutorial integration
  const tutorial = useTutorial('souls', {
    memberLineId: props.memberLineId,
    groupId: props.groupId
  });
  // Data - matching script.js data()
  const positions = ref([]);
  const selectPositoin = ref({});
  const progress = ref([]);
  const hadFoundExhibitIds = ref([]);
  const hadFoundRelatedIds = ref([]);
  const hadFoundTimelineIds = ref([]);
  const hadFoundPositionIds = ref([]);
  const translateItemToHadFound = {
    preExhId: 'hadFoundExhibitIds',
    preRelId: 'hadFoundRelatedIds',
    preTimeId: 'hadFoundTimelineIds',
    prePosId: 'hadFoundPositionIds'
  };
  const lastContent = ref('解鎖記憶');
  const story_id = ref('');
  const selectConversation = ref({});

  const icon = reactive({
    user: '',
    default_location: '',
    default_location_active: '',
    locked_location: '',
    locked_location_active: '',
    conversation_avatar: '',
    conversation_background: '',
    conversation_select_1: '',
    conversation_select_2: '',
    conversation_select_3: '',
    conversation_repeat: ''
  });

  const background = reactive({
    mapBg: '',
    mainBg: ''
  });

  const bannerPropsRef = reactive({ ...bannerProps });

  // Methods - matching script.js methods
  const initViewData = () => {
    const topicInfo = getters.getTopicInfo('position');
    bannerPropsRef.imgSrc = topicInfo.banner;
    bannerPropsRef.content = sampleContent(topicInfo.banner_content);
    bannerPropsRef.styles = "top: 50px; left: 155px;";
    bannerPropsRef.banner_avatar = topicInfo.banner_avatar;
    background.mapBg = topicInfo.map;
    background.mainBg = topicInfo.background;
    Vue.set(icon, 'default_location', topicInfo.default_location);
    Vue.set(icon, 'default_location_active', topicInfo.default_location_active);
    Vue.set(icon, 'locked_location', topicInfo.locked_location);
    Vue.set(icon, 'locked_location_active', topicInfo.locked_location_active);
    Vue.set(icon, 'conversation_avatar', topicInfo.conversation_avatar);
    Vue.set(icon, 'conversation_background', topicInfo.conversation_background);
    Vue.set(icon, 'conversation_select_1', topicInfo.conversation_select_1);
    Vue.set(icon, 'conversation_select_2', topicInfo.conversation_select_2);
    Vue.set(icon, 'conversation_select_3', topicInfo.conversation_select_3);
    Vue.set(icon, 'conversation_repeat', topicInfo.conversation_repeat);
  };

  const getLocationIcon = (position) => {
    const isLock = !isUnlocked(position);
    const isActive = position.tag === selectPositoin.value.tag;
    if (isLock && isActive) return icon.locked_location_active;
    if (isLock) return icon.locked_location;
    if (isActive) return icon.default_location_active;
    return icon.default_location;
  };

  const sampleContent = (contents) => {
    if (!contents || !contents.length) return '';
    return contents[Math.floor(Math.random() * contents.length)];
  };

  const init = (data) => {
    positions.value = data.positions;
    progress.value = data.progress;
    hadFoundExhibitIds.value = data.hadFound.exhibitIds;
    hadFoundRelatedIds.value = data.hadFound.relatedIds;
    hadFoundPositionIds.value = data.hadFound.positionIds;
    hadFoundTimelineIds.value = data.hadFound.timelineIds;
    if (data.demo && props.updateFinishedDemo) {
      props.updateFinishedDemo();
    }
  };

  const isUnlocked = (position) => {
    if (!position.id) return true;

    const hadFoundRefs = {
      hadFoundExhibitIds: hadFoundExhibitIds.value,
      hadFoundRelatedIds: hadFoundRelatedIds.value,
      hadFoundTimelineIds: hadFoundTimelineIds.value,
      hadFoundPositionIds: hadFoundPositionIds.value
    };

    const unlocked = Object.keys(translateItemToHadFound).every(lockItem => {
      if (!position[lockItem] || position[lockItem].length === 0) return true;
      return position[lockItem].every(lock => {
        return hadFoundRefs[translateItemToHadFound[lockItem]].includes(lock);
      });
    });
    return unlocked;
  };

  const changeLine = (content) => {
    if (!content) return;
    return content.replaceAll('\n', '<br>');
  };

  const updatedFoundList = (newItem) => {
    if (progress.value.includes(newItem.id)) return;
    progress.value.push(newItem.id);
  };

  const updateReceivedHadFoundItem = (itemType, item) => {
    if (!item) return;

    // Map itemType to the correct ref
    switch (itemType) {
      case 'hadFoundExhibitIds':
        hadFoundExhibitIds.value.push(item.id);
        break;
      case 'hadFoundRelatedIds':
        hadFoundRelatedIds.value.push(item.id);
        break;
      case 'hadFoundTimelineIds':
        hadFoundTimelineIds.value.push(item.id);
        break;
      case 'hadFoundPositionIds':
        hadFoundPositionIds.value.push(item.id);
        break;
    }
  };

  const setSelectPosition = (tag) => {
    selectPositoin.value = positions.value.find(position => position.tag === tag);
    if (selectPositoin.value && selectPositoin.value.conversations) {
      selectConversation.value = selectPositoin.value.conversations[0];
    }

    // Advance tutorial if waiting for position selection
    advanceTutorialOnSelectPosition();
  };

  // Helper to advance tutorial on position selection
  const advanceTutorialOnSelectPosition = () => {
    if (tutorial.isActive.value &&
        tutorial.currentStep.value &&
        tutorial.currentStep.value.waitForAction === 'select-position') {
      // Validate that user clicked the correct position
      const expectedPosition = '獵戶座山莊．房間';
      if (selectPositoin.value && selectPositoin.value.name !== expectedPosition) {
        alert(`請點擊「${expectedPosition}」來繼續教學！`);
        return;
      }
      setTimeout(() => {
        tutorial.nextStep();
      }, 300);
    }
  };

  const setSelectConversation = (con) => {
    if (selectPositoin.value && selectPositoin.value.conversations) {
      selectConversation.value = selectPositoin.value.conversations.find(
        conversation => conversation.pre_con == con
      );

      // Check if "解鎖記憶" option is now visible (pre_con === 100)
      if (selectConversation.value && selectConversation.value.pre_con === 100) {
        advanceTutorialOnUnlockMemoryVisible();
      }
    }
  };

  // Helper to advance tutorial when "解鎖記憶" becomes visible
  const advanceTutorialOnUnlockMemoryVisible = () => {
    if (tutorial.isActive.value &&
        tutorial.currentStep.value &&
        tutorial.currentStep.value.waitForAction === 'unlock-memory-visible') {
      setTimeout(() => {
        tutorial.nextStep();
      }, 300);
    }
  };

  const resetSelectConversation = () => {
    if (!hadFoundPositionIds.value.includes(selectPositoin.value.id)) {
      if (props.updateGroupStoryShip) {
        props.updateGroupStoryShip('position', selectPositoin.value.name);
      }
    }
    if (selectPositoin.value && selectPositoin.value.conversations) {
      selectConversation.value = selectPositoin.value.conversations[0];
    }

    // Advance tutorial when user clicks "解鎖記憶"
    advanceTutorialOnUnlockMemory();
  };

  // Helper to advance tutorial when user clicks "解鎖記憶"
  const advanceTutorialOnUnlockMemory = () => {
    if (tutorial.isActive.value &&
        tutorial.currentStep.value &&
        tutorial.currentStep.value.waitForAction === 'unlock-memory') {
      setTimeout(() => {
        tutorial.nextStep();
      }, 300);
    }
  };

  const onLoaded = () => {
    mutations.closeLoading();
  };

  // Lifecycle - matching script.js created/mounted/beforeDestroy
  onMounted(async () => {
    initViewData();
    try {
      const response = await axios.get(
        `${HOST_URL}${API_V2}/groups/${props.groupId || props.memberLineId}/positions?line_id=${props.memberLineId}`
      );
      init(response.data);

      // Initialize tutorial from API response
      tutorial.initFromApiResponse(response.data);
    } catch (error) {
      console.log(error);
      console.log(`遊戲服務中斷: ${error.message}`);
    }

    eventBus.$on('received', updateReceivedHadFoundItem);
  });

  onBeforeUnmount(() => {
    eventBus.$off('received', updateReceivedHadFoundItem);
  });

  // Return everything the template needs - matching script.js exactly
  return {
    // Data
    positions,
    selectPositoin,
    progress,
    hadFoundExhibitIds,
    hadFoundRelatedIds,
    hadFoundTimelineIds,
    hadFoundPositionIds,
    lastContent,
    story_id,
    selectConversation,
    icon,
    background,
    bannerProps: bannerPropsRef,

    // Methods
    initViewData,
    getLocationIcon,
    sampleContent,
    init,
    isUnlocked,
    changeLine,
    updatedFoundList,
    updateReceivedHadFoundItem,
    setSelectPosition,
    setSelectConversation,
    resetSelectConversation,
    onLoaded,

    // Tutorial
    tutorial
  };
}
